import React, { useState } from "react";
import { Check, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const PricingSection = () => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const sectionData = labels.menuPages?.howItWorks?.pricingSection || {};
  const sellerData = sectionData.seller || {};
  const buyerData = sectionData.buyer || {};
  const dialogData = sectionData.dialog || {};
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <section className="py-20 bg-gradient-to-b from-black to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {sectionData.title || "Transparent Prissättning"}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {sectionData.description ||
                "Inga dolda avgifter. Inga överraskningar. Bara ärlig och transparent prissättning."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* För Säljare */}
            <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <Star className="w-8 h-8 text-yellow-300 mr-3" />
                  <h3 className="text-3xl font-bold">
                    {sellerData.title || "För Säljare"}
                  </h3>
                </div>

                <div className="text-center mb-8">
                  <div className="text-6xl font-bold mb-2">
                    {sellerData.price || "GRATIS"}
                  </div>
                  <div className="text-xl opacity-90">
                    {sellerData.priceSubtitle || "Kostnadsfri värdering"}
                  </div>
                </div>

                <p className="text-center mb-6 text-lg leading-relaxed">
                  {sellerData.description ||
                    "Sälj din bil på ett enkelt, transparent och tryggt sätt."}
                </p>

                <ul className="space-y-3 mb-8">
                  {(sellerData.features || []).map(
                    (feature: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-emerald-200 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ),
                  )}
                </ul>

                <div className="bg-white/20 rounded-2xl p-4 text-center mb-4">
                  <div className="font-semibold mb-2">
                    {sellerData.disclaimer ||
                      "Du betalar endast om bilen blir såld."}
                  </div>
                  <div className="text-sm opacity-90">
                    {sellerData.disclaimerSub || "Inga dolda avgifter."}
                  </div>
                </div>

                <button
                  onClick={() => setIsDialogOpen(true)}
                  className="w-full text-sm text-emerald-100 hover:text-white underline transition-colors"
                >
                  {sellerData.showFees || "Visa avgifter & villkor"}
                </button>
              </div>
            </div>

            {/* För Köpare */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <Star className="w-8 h-8 text-yellow-300 mr-3" />
                  <h3 className="text-3xl font-bold">
                    {buyerData.title || "För Köpare"}
                  </h3>
                </div>

                <div className="text-center mb-8">
                  <div className="text-4xl font-bold mb-2 leading-tight">
                    {buyerData.price || "Rättvis Prissättning"}
                  </div>
                  <div className="text-xl opacity-90">
                    {buyerData.priceSubtitle || "Transparent kostnadsstruktur"}
                  </div>
                </div>

                <p className="text-center mb-6 text-lg leading-relaxed">
                  {buyerData.description ||
                    "Köp bil via auktion med full insyn och tydlig information."}
                </p>

                <ul className="space-y-3 mb-8">
                  {(buyerData.features || []).map(
                    (feature: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-blue-200 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ),
                  )}
                </ul>

                <div className="bg-white/20 rounded-2xl p-4 mb-4">
                  <div className="font-semibold mb-2 text-center">
                    {buyerData.deliveryFee?.title ||
                      "Leveransavgift vid leverans"}
                  </div>
                  <div className="text-sm opacity-90 text-center">
                    {buyerData.deliveryFee?.description ||
                      "Inkluderar transport, försäkring och säker hantering"}
                  </div>
                </div>

                <div className="text-center mb-4">
                  <div className="font-semibold mb-2">
                    {buyerData.disclaimer ||
                      "Du betalar endast vid genomförd affär."}
                  </div>
                </div>

                <button
                  onClick={() => setIsDialogOpen(true)}
                  className="w-full text-sm text-blue-100 hover:text-white underline transition-colors"
                >
                  {buyerData.showFees || "Visa avgifter & villkor"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pop-up Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl md:text-3xl font-bold text-gray-900">
              {dialogData.title || "Avgifter hos BILENIA Auktioner"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 text-gray-700">
            <p className="text-lg">
              {dialogData.intro ||
                "Hos BILENIA betalar du endast när en affär genomförs. Alla avgifter är tydliga och utan dolda kostnader."}
            </p>

            {/* För säljare */}
            {dialogData.seller && (
              <div className="bg-emerald-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {dialogData.seller.title || "För säljare (privatperson)"}
                </h3>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {dialogData.seller.feeTitle || "Säljaravgift:"}
                  </h4>
                  <p className="mb-3">
                    {dialogData.seller.feeDescription ||
                      "En procentbaserad avgift som tas ut endast vid lyckad försäljning."}
                  </p>
                  <ul className="space-y-2 ml-4">
                    {(dialogData.seller.feePoints || []).map(
                      (point: string, index: number) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{point}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
                {(dialogData.seller.benefits || []).map(
                  (benefit: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 text-emerald-700"
                    >
                      <Check className="w-5 h-5" />
                      <span className="font-medium">{benefit}</span>
                    </div>
                  ),
                )}
              </div>
            )}

            {/* För köpare */}
            {dialogData.buyer && (
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {dialogData.buyer.title || "För köpare (privatperson)"}
                </h3>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {dialogData.buyer.feeTitle || "Köparavgift:"}
                  </h4>
                  <p className="mb-3">
                    {dialogData.buyer.feeDescription ||
                      "En procentbaserad avgift som betalas endast vid vunnen auktion."}
                  </p>
                  <ul className="space-y-2 ml-4">
                    {(dialogData.buyer.feePoints || []).map(
                      (point: string, index: number) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{point}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
                {(dialogData.buyer.benefits || []).map(
                  (benefit: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 text-blue-700"
                    >
                      <Check className="w-5 h-5" />
                      <span className="font-medium">{benefit}</span>
                    </div>
                  ),
                )}
              </div>
            )}

            {/* För bilhandlare */}
            {dialogData.dealer && (
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {dialogData.dealer.title || "För bilhandlare"}
                </h3>
                <p className="mb-3">
                  {dialogData.dealer.description ||
                    "BILENIA erbjuder särskilda villkor för bilhandlare."}
                </p>
                <ul className="space-y-2 ml-4 mb-4">
                  {(dialogData.dealer.points || []).map(
                    (point: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{point}</span>
                      </li>
                    ),
                  )}
                </ul>
                {dialogData.dealer.note && (
                  <p className="text-purple-700 font-medium">
                    {dialogData.dealer.note}
                  </p>
                )}
              </div>
            )}

            {/* Viktigt att veta */}
            {dialogData.important && (
              <div className="bg-gray-100 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {dialogData.important.title || "Viktigt att veta"}
                </h3>
                <ul className="space-y-2 ml-4">
                  {(dialogData.important.points || []).map(
                    (point: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{point}</span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PricingSection;
